import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  TabContext,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  Student,
  StudentEditResource,
  StudentViewResource,
  TenantInfo,
  TextingOptIn as TextingOptInModel
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { Services } from "./Services/Services";
import { StudentPageHeader } from "./StudentPageHeader";
import { StudentProfile } from "./StudentProfile/StudentProfile";
import { StudentTabs } from "./StudentTabs";
import { StudentTranscript } from "./StudentTranscript/StudentTranscript";
import { StudentForms } from "./StudentForms";
import { StudentApplications } from "./StudentApplications";
import { NscColleges } from "./NscColleges";
import { StudentAprUb } from "./StudentAprUb";
import { StudentFiles } from "./StudentFiles";
import { setAppToast } from "redux/reducers/appSlice";
import { useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { TextingConversationsPage } from "pages/texting/TextingConversationsPage/TextingConversationsPage";

const routeName = "student";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: StudentTab | undefined = undefined;
}

export const studentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Student",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export interface StudentTabItem {
  id: string;
  name: string;
}

export type StudentTab = "Profile" | "Services" | "Transcript" | "Forms" | "Applications" | "Post-Secondary" | "APR-UB" | "Files" | "Texting";

export const StudentPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Student;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<StudentTab>(optionalParams.tab || "Profile");
  const [student, setStudent] = useState<Student>({} as Student);
  const lastSavedStudent = useRef<Student>({} as Student);
  const [textingOptin, setTextingOptIn] = useState<TextingOptInModel>();
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [student] });
  const [viewResource, setViewResource] = useState<StudentViewResource>({});
  const [editResource, setEditResource] = useState<StudentEditResource>({} as StudentEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const [visitedTabs, setVisitedTabs] = useState<StudentTab[]>([]);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();
  const [aprRefreshKey, setAprRefreshKey] = useState(0);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
    fetchTenantInfo();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.students.get(id).then((response) => {
      lastSavedStudent.current = response;
      setStudent(response);
      resetUnsavedChanges([response]);
    });
    ApiService.textingOptIn.getByStudentId(id).then((response) => {
      setTextingOptIn(response);
    });
  };

  const fetchTenantInfo = () => {
    ApiService.textingConversations.getTenantTextingInfo().then((response) => {
      setTenantInfo(response);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.students.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.students.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const cancelEdit = () => {
    const id = lastSavedStudent.current.id || student.id;
    setValidationErrors({});
    setStudent(lastSavedStudent.current);
    resetUnsavedChanges([lastSavedStudent.current]);
    reroute(id, "view");
  };

  const save = () => {
    const id = student.id;
    setValidationErrors({});

    if (id > 0) {
      ApiService.students
        .saveUpdate(student)
        .then(() => {
          reroute(id, "view");
          fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    } else {
      ApiService.students
        .saveCreate(student)
        .then((response) => {
          const id = response;
          reroute(id, "view");
          fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    }
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <StudentPageHeader
            rootPermission={ROOT_PERMISSION}
            crudMode={params.crudMode}
            fetch={fetch}
            reroute={reroute}
            setCurrentTab={setCurrentTab}
            student={student}
            viewResource={viewResource}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
          />
        }
        tabs={
          <StudentTabs
            student={student}
            textingOptIn={textingOptin}
            viewResource={viewResource}
            tenantInfo={tenantInfo}
            setCurrentTab={setCurrentTab}
          />
        }
      >
        <Box hidden={currentTab !== "Profile"}>
          <StudentProfile
            rootPermission={ROOT_PERMISSION}
            textingOptIn={textingOptin}
            tenantTextingInfo={tenantInfo}
            initialLoad={visitedTabs.includes("Profile")}
            id={params.id}
            student={student}
            setStudent={setStudent}
            crudMode={params.crudMode}
            viewResource={viewResource}
            editResource={editResource}
            validationErrors={validationErrors}
            save={save}
            cancelEdit={cancelEdit}
            reroute={reroute}
            unsavedChanges={unsavedChanges}
          />
        </Box>

        <Box hidden={currentTab !== "Services"} height="100%" overflow="auto">
          <Services
            initialLoad={visitedTabs.includes("Services")}
            studentId={params.id}
            onGetList={() => fetchViewResource(params.id)}
          />
        </Box>

        <Box hidden={currentTab !== "Transcript"} height="100%" overflow="auto">
          <StudentTranscript
            initialLoad={visitedTabs.includes("Transcript")}
            studentId={params.id}
            activeSchoolYear={viewResource?.activeSchoolYear}
            onGetList={() => fetchViewResource(params.id)}
            onGpaGetList={() => setAprRefreshKey((key) => key + 1)}
            viewResource={viewResource}
          />
        </Box>

        <Box hidden={currentTab !== "Forms"} height="100%" overflow="auto">
          <StudentForms
            initialLoad={visitedTabs.includes("Forms")}
            studentId={params.id}
            activeSchoolYear={viewResource?.activeSchoolYear}
            onGetList={() => fetchViewResource(params.id)}
            viewResource={viewResource}
          />
        </Box>

        <Box hidden={currentTab !== "Applications"} height="100%" overflow="auto">
          <StudentApplications
            initialLoad={visitedTabs.includes("Applications")}
            studentId={params.id}
            activeSchoolYear={viewResource?.activeSchoolYear}
            onGetList={() => fetchViewResource(params.id)}
            viewResource={viewResource}
          />
        </Box>

        <Box hidden={currentTab !== "Files"} height="100%" overflow="auto">
          <StudentFiles
            initialLoad={visitedTabs.includes("Files")}
            studentId={params.id}
            activeSchoolYear={viewResource?.activeSchoolYear}
            onGetList={() => fetchViewResource(params.id)}
            viewResource={viewResource}
          />
        </Box>

        <Box hidden={currentTab !== "Post-Secondary"} height="100%" overflow="auto">
          {student.customId1 && (
            <NscColleges
              initialLoad={visitedTabs.includes("Post-Secondary")}
              identification={student.customId1}
              onGetList={() => fetchViewResource(params.id)}
            />
          )}
        </Box>

        <Box hidden={currentTab !== "APR-UB"} height="100%" overflow="auto">
          <StudentAprUb initialLoad={visitedTabs.includes("APR-UB")} studentId={params.id} refreshKey={aprRefreshKey} />
        </Box>

        <Box hidden={currentTab !== "Texting"} height="100%" overflow="auto">
          {visitedTabs.includes("Texting") && <TextingConversationsPage studentId={params.id} embedded />}
        </Box>
      </Page>
    </TabContext>
  );
};
